import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';

interface DeviceAisTabProps {
  deviceId: string;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatPosition(lat: number | null, lon: number | null): string {
  if (lat == null || lon == null) return '—';
  const latDir = lat >= 0 ? 'N' : 'S';
  const lonDir = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}${latDir} ${Math.abs(lon).toFixed(4)}${lonDir}`;
}

export default function DeviceAisTab({ deviceId }: DeviceAisTabProps) {
  const { data: vessels, isLoading } = useQuery({
    queryKey: ['device-ais-vessels', deviceId],
    queryFn: () => api.adminGetDeviceAisVessels(deviceId),
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-300">AIS Vessels</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Vessels observed by this device via AIS
          </p>
        </div>
        {vessels && vessels.length > 0 && (
          <span className="text-xs text-slate-400">
            {vessels.length} vessel{vessels.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 animate-pulse">
          <div className="h-4 w-48 bg-slate-700 rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-slate-700/50 rounded" />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!vessels || vessels.length === 0) && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12 text-center">
          <p className="text-slate-400 text-sm">No AIS vessels observed by this device</p>
          <p className="text-slate-500 text-xs mt-1">
            AIS data is reported via heartbeat when the device detects nearby vessels.
          </p>
        </div>
      )}

      {/* Vessel Table */}
      {!isLoading && vessels && vessels.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">MMSI</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Position</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">SOG</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">COG</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">Sightings</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Last Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {vessels.map((v: any) => {
                  const isRecent = v.last_seen_at &&
                    (Date.now() - new Date(v.last_seen_at).getTime()) < 30 * 60 * 1000;
                  return (
                    <tr key={v.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{v.mmsi}</td>
                      <td className="px-4 py-3 text-white font-medium">
                        {v.ship_name || <span className="text-slate-500 italic">Unknown</span>}
                        {v.call_sign && (
                          <span className="ml-2 text-xs text-slate-500">({v.call_sign})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-xs">{v.ship_type || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">
                        {formatPosition(v.last_lat, v.last_lon)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">
                        {v.last_sog != null ? `${v.last_sog.toFixed(1)} kn` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">
                        {v.last_cog != null ? `${v.last_cog.toFixed(0)}°` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">
                        {v.sighting_count?.toLocaleString() || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${isRecent ? 'text-green-400' : 'text-slate-400'}`}>
                          {v.last_seen_at ? formatRelativeTime(v.last_seen_at) : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
